import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import { useAuth } from '../auth/AuthContext.jsx';
import { teamApi } from '../api/team.js';
import { Copyable } from '../components/ui/Copyable.jsx';
import { NotificationsBox } from '../components/NotificationsBox.jsx';

function ChangePasswordBox() {
  const [oldPwd, setOldPwd] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  async function handle(e) {
    e.preventDefault();
    setMsg(''); setErr(''); setBusy(true);
    try {
      await teamApi.changePassword(oldPwd, newPwd);
      setMsg('Parolă schimbată cu succes.');
      setOldPwd(''); setNewPwd('');
    } catch (ex) {
      setErr(ex.body?.error === 'invalid_credentials' ? 'Parola veche nu e corectă.' : (ex.body?.error || 'Eroare'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handle} className="card space-y-3">
      <h3 className="text-base font-semibold text-ink">Schimbă parola</h3>
      {msg && <div className="rounded-lg border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok">{msg}</div>}
      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}
      <input
        type="password" required placeholder="Parola actuală" value={oldPwd}
        onChange={(e) => setOldPwd(e.target.value)}
        className="w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
      />
      <input
        type="password" required minLength={10} placeholder="Parolă nouă (min. 10)" value={newPwd}
        onChange={(e) => setNewPwd(e.target.value)}
        className="w-full rounded-lg border border-border bg-paper px-3 py-2.5 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
      />
      <button type="submit" disabled={busy} className="btn-ghost border border-border w-full">
        {busy ? 'Se schimbă…' : 'Schimbă parola'}
      </button>
    </form>
  );
}

const ENTITY_TYPES = [
  { value: '', label: '—' },
  { value: 'srl', label: 'SRL' },
  { value: 'pfa', label: 'PFA' },
  { value: 'ii', label: 'Întreprindere Individuală (II)' },
  { value: 'sa', label: 'SA' },
  { value: 'persoana_fizica', label: 'Persoană fizică' },
  { value: 'altele', label: 'Altele' },
];

function SignatureBox() {
  const { user, refresh } = useAuth();
  const sigRef = useRef(null);
  const savedSignature = user?.contract_profile?.signature_data_url || null;
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  async function handleSave() {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      setErr('Desenează o semnătură mai întâi.');
      return;
    }
    setErr(''); setMsg(''); setBusy(true);
    try {
      const dataUrl = sigRef.current.getTrimmedCanvas().toDataURL('image/png');
      const currentProfile = user?.contract_profile || {};
      await teamApi.updateProfile({ ...currentProfile, signature_data_url: dataUrl });
      setMsg('Semnătură salvată. Va apărea pe contractele generate de tine.');
      await refresh();
      sigRef.current?.clear();
    } catch (ex) {
      setErr(ex.body?.error || 'Eroare');
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    if (!confirm('Elimini semnătura salvată? Va trebui să desenezi una nouă pentru a genera contracte.')) return;
    setBusy(true);
    try {
      const currentProfile = user?.contract_profile || {};
      const { signature_data_url: _drop, ...rest } = currentProfile;
      await teamApi.updateProfile({ ...rest, signature_data_url: null });
      await refresh();
      setMsg('Semnătură eliminată.');
    } catch (ex) {
      setErr(ex.body?.error || 'Eroare');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card space-y-3">
      <div>
        <h3 className="text-base font-semibold text-ink">Semnătura ta</h3>
        <p className="mt-1 text-xs text-muted">
          Desenează o dată semnătura ta. Aceasta se aplică automat pe toate contractele de închiriere generate de tine, ca „Proprietar".
        </p>
      </div>

      {msg && <div className="rounded-lg border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok">{msg}</div>}
      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      {savedSignature ? (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">Semnătura actuală</div>
          <div className="rounded-lg border border-border bg-white p-3">
            <img src={savedSignature} alt="Semnătură" className="max-h-24" />
          </div>
          <button onClick={handleClear} disabled={busy} className="btn-ghost mt-2 text-xs text-danger hover:bg-danger/10">
            Șterge și desenează alta
          </button>
        </div>
      ) : (
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted mb-1">
            Desenează semnătura (deget / mouse / stylus)
          </div>
          <div className="rounded-lg border-2 border-dashed border-border bg-white">
            <SignatureCanvas
              ref={sigRef}
              penColor="#0B2027"
              canvasProps={{ className: 'w-full h-40 rounded-lg', style: { touchAction: 'none' } }}
            />
          </div>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => sigRef.current?.clear()} className="btn-ghost text-xs">
              Șterge
            </button>
            <button type="button" onClick={handleSave} disabled={busy} className="btn-primary text-xs">
              {busy ? 'Se salvează…' : 'Salvează semnătura'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContractProfileBox() {
  const { user, refresh } = useAuth();
  const p = user?.contract_profile || {};
  const [form, setForm] = useState({
    name: p.name || '',
    entity_type: p.entity_type || '',
    cui: p.cui || '',
    reg_com: p.reg_com || '',
    adresa: p.adresa || '',
    iban: p.iban || '',
    banca: p.banca || '',
    telefon: p.telefon || '',
    email: p.email || '',
    reprezentant: p.reprezentant || '',
  });
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Re-sincronizează când user se reîncarcă
    if (user?.contract_profile) {
      const p2 = user.contract_profile;
      setForm({
        name: p2.name || '',
        entity_type: p2.entity_type || '',
        cui: p2.cui || '',
        reg_com: p2.reg_com || '',
        adresa: p2.adresa || '',
        iban: p2.iban || '',
        banca: p2.banca || '',
        telefon: p2.telefon || '',
        email: p2.email || '',
        reprezentant: p2.reprezentant || '',
      });
    }
  }, [user?.contract_profile]);

  function onChange(k, v) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function handle(e) {
    e.preventDefault();
    setMsg(''); setErr(''); setBusy(true);
    try {
      await teamApi.updateProfile(form);
      setMsg('Date firmă salvate. Vor apărea pe contractele pe care le generezi.');
      await refresh();
    } catch (ex) {
      if (ex.body?.error === 'validation_error') setErr('Date invalide (verifică email-ul).');
      else setErr(ex.body?.error || 'Eroare');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handle} className="card space-y-3">
      <div>
        <h3 className="text-base font-semibold text-ink">Date firmă / prestator</h3>
        <p className="mt-1 text-xs text-muted">
          Aceste date apar pe contractele de închiriere pe care TU le generezi, ca „Proprietar".
          Poți lăsa câmpurile goale — în contract va apărea doar numele platformei.
        </p>
      </div>

      {msg && <div className="rounded-lg border border-ok/30 bg-ok/10 px-3 py-2 text-sm text-ok">{msg}</div>}
      {err && <div className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-sm text-danger">{err}</div>}

      <div className="grid grid-cols-3 gap-2">
        <label className="col-span-2 block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Denumire</span>
          <input
            value={form.name}
            onChange={(e) => onChange('name', e.target.value)}
            placeholder="SC Exemplu SRL / Popescu Ion"
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Formă juridică</span>
          <select
            value={form.entity_type}
            onChange={(e) => onChange('entity_type', e.target.value)}
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2"
          >
            {ENTITY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">CUI / CIF / CNP</span>
          <input
            value={form.cui}
            onChange={(e) => onChange('cui', e.target.value)}
            placeholder="RO12345678"
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Reg. Comerțului</span>
          <input
            value={form.reg_com}
            onChange={(e) => onChange('reg_com', e.target.value)}
            placeholder="J40/1234/2020"
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Sediu / Adresă</span>
        <input
          value={form.adresa}
          onChange={(e) => onChange('adresa', e.target.value)}
          placeholder="Str. Exemplu nr. 1, București, sector 1"
          className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
        />
      </label>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">IBAN</span>
          <input
            value={form.iban}
            onChange={(e) => onChange('iban', e.target.value.toUpperCase().replace(/\s/g, ''))}
            placeholder="RO00 BANK 0000 ..."
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Banca</span>
          <input
            value={form.banca}
            onChange={(e) => onChange('banca', e.target.value)}
            placeholder="BCR / ING / Raiffeisen"
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Telefon firmă</span>
          <input
            value={form.telefon}
            onChange={(e) => onChange('telefon', e.target.value)}
            placeholder="+40 7XX XXX XXX"
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>
        <label className="block">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Email firmă</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => onChange('email', e.target.value)}
            placeholder="contact@exemplu.ro"
            className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 font-mono focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted">Reprezentant</span>
        <input
          value={form.reprezentant}
          onChange={(e) => onChange('reprezentant', e.target.value)}
          placeholder="Nume administrator / titular PFA"
          className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 focus:border-teal focus:outline-none focus:ring-2 focus:ring-teal/20"
        />
      </label>

      <button type="submit" disabled={busy} className="btn-primary w-full">
        {busy ? 'Se salvează…' : 'Salvează datele'}
      </button>
    </form>
  );
}

export function Account() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <div className="space-y-6">
      <h2 className="text-3xl font-semibold tracking-tight text-ink">Contul meu</h2>

      <div className="card space-y-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Email</div>
          <Copyable value={user?.email || ''} as="button" className="block font-mono text-sm hover:text-teal-bright">
            {user?.email}
          </Copyable>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Nume</div>
          <div>{user?.name}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Rol</div>
          <div>{user?.role === 'admin' ? 'Administrator' : 'Utilizator'}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Platforma</div>
          <div>{user?.tenant?.name}</div>
        </div>
        <button onClick={logout} className="btn-ghost w-full border border-border">Deconectare</button>
      </div>

      <NotificationsBox />

      <ContractProfileBox />

      <SignatureBox />

      <ChangePasswordBox />

      {isAdmin && (
        <Link
          to="/users"
          className="card flex items-center justify-between bg-ink text-cream hover:bg-ink-soft"
        >
          <div>
            <div className="text-base font-semibold">Utilizatori</div>
            <div className="text-xs text-cream/70">Invită, editează sau șterge utilizatori</div>
          </div>
          <span className="text-2xl">→</span>
        </Link>
      )}
    </div>
  );
}
